// useCountyAutocomplete.js
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import api from "../../../api/axios";

const initialState = {
  query: "",
  suggestions: [],
  activeIndex: -1,
  isLoading: false,
  selectedCounty: null,
};

const patchReducer = (s, a) => ({ ...s, ...a });

export function useCountyAutocomplete() {
  const [state, dispatch] = useReducer(patchReducer, initialState);
  const patch = useCallback((p) => dispatch(p), []);

  // UI refs for dropdown
  const suggestionItemRefs = useRef([]);

  // Request guards for debouncing
  const requestRidRef = useRef(0);

  // Dropdown suppression: after selecting, do NOT fetch predictions until user types again
  const suppressPredictionsRef = useRef(false);

  // Fetch county suggestions from API
  useEffect(() => {
    // IMPORTANT: keep dropdown closed after selection until user types again
    if (suppressPredictionsRef.current) {
      if (state.suggestions.length || state.activeIndex !== -1) {
        patch({ suggestions: [], activeIndex: -1 });
      }
      return;
    }

    const q = (state.query || "").trim();

    if (q.length < 2) {
      if (state.suggestions.length || state.activeIndex !== -1) {
        patch({ suggestions: [], activeIndex: -1 });
      }
      return;
    }

    const rid = ++requestRidRef.current;

    const t = setTimeout(async () => {
      try {
        patch({ isLoading: true });
        const resp = await api.post("/autocomplete-fips", {
          county_name: q,
        });

        if (rid !== requestRidRef.current) return;
       
        if (resp?.data?.status === "success" && resp?.data?.data) {
          // Convert the response data object to an array of suggestions
          const suggestions = Object.entries(resp.data.data).map(([code, name]) => ({
            code,
            name,
            displayText: name,
          }));
         
          patch({ suggestions, activeIndex: -1, isLoading: false });
        } else {
          patch({ suggestions: [], activeIndex: -1, isLoading: false });
        }
      } catch (error) {
        if (rid === requestRidRef.current) {
          patch({ suggestions: [], activeIndex: -1, isLoading: false });
        }
        console.error("Error fetching county suggestions:", error);
      }
    }, 350);

    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- state.activeIndex/suggestions.length intentionally excluded
  }, [state.query, patch]);

  const onChange = useCallback(
    (e) => {
      // user typing -> re-enable predictions
      suppressPredictionsRef.current = false;

      const next = e.target.value;
      patch({
        query: next,
        activeIndex: -1,
        selectedCounty: null,
      });
    },
    [patch]
  );

  const onBlur = useCallback(() => {
    // Small delay to allow for suggestion clicks
    setTimeout(() => patch({ suggestions: [] }), 150);
  }, [patch]);

  const setActiveIndex = useCallback(
    (idx) => patch({ activeIndex: idx }),
    [patch]
  );

  const selectSuggestion = useCallback(
    (suggestion) => {
      // IMPORTANT: after selection, keep predictions suppressed until user types
      suppressPredictionsRef.current = true;

      patch({
        query: suggestion.displayText,
        suggestions: [],
        activeIndex: -1,
        selectedCounty: suggestion,
      });
    },
    [patch]
  );

  const onKeyDown = useCallback(
    (e) => {
      const { suggestions, activeIndex } = state;
      if (!suggestions.length) return;

      const max = suggestions.length - 1;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        patch({ activeIndex: activeIndex < max ? activeIndex + 1 : max });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        patch({ activeIndex: activeIndex > 0 ? activeIndex - 1 : -1 });
      } else if (e.key === "Escape") {
        patch({ suggestions: [], activeIndex: -1 });
      } else if (e.key === "Enter") {
        if (activeIndex >= 0 && activeIndex <= max) {
          e.preventDefault();
          selectSuggestion(suggestions[activeIndex]);
        }
      }
    },
    [patch, selectSuggestion, state]
  );

  // Scroll active suggestion into view
  useEffect(() => {
    const el = suggestionItemRefs.current[state.activeIndex];
    el?.scrollIntoView?.({ block: "nearest" });
  }, [state.activeIndex]);

  return useMemo(
    () => ({
      ...state,
      onChange,
      onBlur,
      onKeyDown,
      selectSuggestion,
      setActiveIndex,
      suggestionItemRefs,
    }),
    [state, onChange, onBlur, onKeyDown, selectSuggestion, setActiveIndex]
  );
}